import React, { useState } from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faScaleBalanced,
    faGraduationCap,
    faGem,
    faBullseye,
    faEye,
    faHandshake,
    faBuildingColumns,
    faUsers,
    faGlobe,
    faMapMarkerAlt,
    faCalendarAlt,
    faAward,
    faLightbulb,
    faBookOpen,
    faGavel,
    faChartLine,
    faShieldAlt,
    faQuoteLeft,
    faChevronRight,
    faExternalLinkAlt
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1],
            stiffness: 100
        }
    }
};

const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 1,
            ease: [0.22, 1, 0.36, 1]
        }
    }
};

const scaleIn = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: {
            duration: 0.7,
            ease: "easeOut"
        }
    }
};

const practiceAreas = [
    { icon: faScaleBalanced, name: "Corporate & Commercial Disputes" },
    { icon: faGem, name: "Arbitration & Mediation" },
    { icon: faBuildingColumns, name: "Constitutional Law" },
    { icon: faGavel, name: "Criminal Law (Bails, Quashing)" },
    { icon: faChartLine, name: "White-Collar Crimes" },
    { icon: faUsers, name: "Service Laws" },
    { icon: faBookOpen, name: "Direct & Indirect Taxation" },
    { icon: faLightbulb, name: "Intellectual Property" },
    { icon: faShieldAlt, name: "Banking & Securitization Laws" },
    { icon: faHandshake, name: "Consumer Protection Laws" },
    { icon: faUsers, name: "Labour Laws" },
    { icon: faMapMarkerAlt, name: "Land Disputes" }
];

const courtsTribunals = [
    { name: "Supreme Court of India", level: "Apex Court" },
    { name: "Patna High Court", level: "High Court" },
    // { name: "Jharkhand High Court", level: "High Court" },
    // { name: "Rajasthan High Court", level: "High Court" },
    { name: "District Courts", level: "District Level" },
    { name: "Magistrates Courts", level: "Local Courts" },
    { name: "Consumer Redressal Forum", level: "Tribunal" },
    // { name: "National Company Law Tribunal (NCLT)", level: "Special Tribunal" },
    { name: "Central/State Administrative Tribunals", level: "Tribunal" },
    { name: "Debt Recovery Tribunals", level: "Special Tribunal" },
    // { name: "Tax Tribunals", level: "Special Tribunal" }
];

const founders = [
    {
        name: "Nikhil Kumar Agrawal",
        qualification: "Advocate-on-Record, Patna High Court",
        education: "University College London (UCL)",
        expertise: "Civil, Criminal and Constitutional Litigation",
        year: "2008",
        description: "Founding Partner specializing in civil and white-collar crimes "
    },
    {
        name: "Aditi Hansaria",
        qualification: "Advocate-on-Record, Patna High Court",
        education:"London School of Economics (LSE)",
        expertise: "Civil, Corporate and Constitutional Law",
        year: "2008",
        description: "Founding Partner specializing in civil defence, constitutional matters and commercial disputes"
    }
];

const HoverCard = ({ children, className = "", style = {}, ...props }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <motion.div
            className={`position-relative overflow-hidden ${className}`}
            style={style}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            whileHover={{
                y: -8,
                transition: { duration: 0.3, ease: "easeOut" }
            }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            {...props}
        >
            {/* Hover glow effect */}
            <motion.div
                className="position-absolute top-0 left-0 w-full h-full"
                initial={false}
                animate={{
                    opacity: isHovered ? 0.1 : 0,
                    scale: isHovered ? 1.05 : 1
                }}
                transition={{ duration: 0.3 }}
                style={{
                    background: "radial-gradient(circle at center, var(--gold) 0%, transparent 70%)",
                    zIndex: 0
                }}
            />

            {/* Border animation */}
            <motion.div
                className="position-absolute top-0 left-0 w-full h-full"
                initial={false}
                animate={{
                    opacity: isHovered ? 1 : 0
                }}
                transition={{ duration: 0.3 }}
                style={{
                    border: "2px solid var(--gold)",
                    borderRadius: "inherit",
                    zIndex: 1,
                    pointerEvents: "none"
                }}
            />

            {children}
        </motion.div>
    );
};



export const OurStory = () => {
    const [hoveredArea, setHoveredArea] = useState(null);
    const [hoveredCourt, setHoveredCourt] = useState(null);
    const navigate=useNavigate();
    return (
        <div style={{
            backgroundColor: "var(--cream)",
            minHeight: "100vh",
            padding: "130px 0 60px",
            position: "relative"
        }}>
            {/* Animated Background Elements */}
            <div className="position-fixed top-0 start-0 w-100 h-100" style={{ zIndex: 0, pointerEvents: "none", overflow: "hidden" }}>
                {/* Floating particles */}
                {[...Array(15)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="position-absolute"
                        style={{
                            width: "4px",
                            height: "4px",
                            backgroundColor: "var(--gold-light)",
                            borderRadius: "50%",
                            opacity: 0.1,
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`
                        }}
                        animate={{
                            y: [0, -20, 0],
                            x: [0, Math.random() * 10 - 5, 0]
                        }}
                        transition={{
                            duration: 3 + Math.random() * 2,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: Math.random() * 2
                        }}
                    />
                ))}

                {/* Moving gradient orbs */}
                <motion.div
                    className="position-absolute"
                    style={{
                        width: "300px",
                        height: "300px",
                        background: "radial-gradient(circle, var(--gold) 0%, transparent 70%)",
                        opacity: 0.02,
                        borderRadius: "50%"
                    }}
                    animate={{
                        x: [0, 100, 0],
                        y: [0, 50, 0]
                    }}
                    transition={{
                        duration: 20,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                />

                <motion.div
                    className="position-absolute"
                    style={{
                        width: "200px",
                        height: "200px",
                        background: "radial-gradient(circle, var(--primary-blue) 0%, transparent 70%)",
                        opacity: 0.02,
                        borderRadius: "50%",
                        right: "10%",
                        bottom: "20%"
                    }}
                    animate={{
                        x: [0, -80, 0],
                        y: [0, -40, 0]
                    }}
                    transition={{
                        duration: 15,
                        repeat: Infinity,
                        ease: "linear",
                        delay: 1
                    }}
                />
            </div>

            <div className="container position-relative" style={{ zIndex: 1 }}>
                {/* Hero Section with enhanced animations */}
                <motion.div
                    className="text-center mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={containerVariants}
                >
                    <motion.div
                        variants={fadeInUp}
                        whileHover={{ scale: 1.02 }}
                        transition={{ type: "spring", stiffness: 300 }}
                    >
                      

                        <span style={{
                            color: "var(--gold)",
                            fontWeight: "600",
                            letterSpacing: "3px",
                            fontSize: "0.9rem",
                            textTransform: "uppercase",
                            display: "inline-block",
                            padding: "8px 20px",
                            border: "1px solid var(--gold-light)",
                            borderRadius: "50px",
                            background: "rgba(184, 134, 11, 0.05)",
                            backdropFilter: "blur(4px)"
                        }}>
                            Our Journey
                        </span>

                        <motion.h4
                            className="fw-bold display-6 display-md-5 mt-2"
                            variants={fadeInUp}
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                fontWeight: 600,
                                color: 'var(--primary-dark)',
                                background: "linear-gradient(135deg, var(--primary-dark) 0%, var(--gold) 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text"
                            }}
                        >
                            RNA Law Chambers <br/>Excellence in Legal Practice
                        </motion.h4>
                        <motion.p
                        className="fs-6 fs-md-5 mx-auto mb-3 mb-md-4"
                        variants={fadeInUp}
                        style={{
                            maxWidth: "750px",
                            color: "var(--text-primary)",
                            lineHeight: 1.7
                        }}
                    >
                        Founded by London-educated Advocates-on-Record, we bring international
                       legal expertise combined with deep understanding of Indian jurisprudence.
                    </motion.p>
                    </motion.div>

                    
                </motion.div>

                {/* Founders Section with enhanced cards */}
                <motion.div
                    className="row g-5 mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                    variants={containerVariants}
                >
                    <div className="col-lg-12">
                        <motion.div variants={itemVariants} className="mb-5">
                            <div className="text-center mb-5">
                                <motion.div
                                    className="d-inline-block mb-4"
                                    whileHover={{ rotate: 360 }}
                                    transition={{ duration: 0.8, ease: "easeInOut" }}
                                >
                                    <div className="p-3 rounded-circle d-inline-flex align-items-center justify-content-center"
                                        style={{
                                            backgroundColor: "rgba(184, 134, 11, 0.1)",
                                            border: "2px solid var(--gold-light)",
                                            width: "40px",
                                            height: "40px"
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faGraduationCap} style={{
                                            color: "var(--gold)",
                                            fontSize: "1rem"
                                        }} />
                                    </div>
                                </motion.div>

                                <h2 className="fw-bold mb-3"
                                    style={{
                                        fontFamily: "'Playfair Display', serif",
                                        color: "var(--primary-dark)",
                                        position: "relative",
                                        display: "inline-block"
                                    }}
                                >
                                    Our Founding Partners
                                    <motion.div
                                className="position-absolute bottom-15 mt-2 left-0 w-100"
                                initial={{ scaleX: 0 }}
                                whileInView={{ scaleX: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, ease: "easeOut" }}
                                style={{
                                    height: "3px",
                                    background: "linear-gradient(90deg, transparent, var(--gold), transparent)",
                                    transformOrigin: "center"
                                }}
                            />
                                </h2>
                                <p className="text-muted" style={{ maxWidth: "600px", margin: "0 auto" }}>
                                    Advocates-on-Record with over +18 Years of  experience
                                </p>
                            </div>

                            <div className="row g-4">
                                {founders.map((founder, index) => (
                                    <motion.div
                                        key={index}
                                        className="col-md-6"
                                        variants={itemVariants}
                                        whileHover={{ scale: 1.02 }}
                                        transition={{ type: "spring", stiffness: 300 }}
                                    >
                                        <HoverCard
                                            className="h-100 p-4 p-md-5 rounded-4"
                                            style={{
                                                backgroundColor: "var(--white)",
                                                border: "1px solid var(--gold-light)",
                                                boxShadow: "var(--shadow-medium)",
                                                minHeight: "320px"
                                            }}
                                        >
                                            <div className="position-relative z-2 h-100 d-flex flex-column">
                                                <div className="mb-4">
                                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                                        <motion.div
                                                            whileHover={{ x: 5 }}
                                                            transition={{ type: "spring", stiffness: 400 }}
                                                        >
                                                            <h4 className="fw-bold mb-0" style={{ color: "var(--primary-dark)" }}>
                                                                {founder.name}
                                                            </h4>
                                                        </motion.div>

                                                        <motion.div
                                                            whileHover={{ scale: 1.1, rotate: 5 }}
                                                            transition={{ type: "spring", stiffness: 400 }}
                                                        >
                                                            <div className="badge px-3 py-2"
                                                                style={{
                                                                    backgroundColor: "rgba(184, 134, 11, 0.1)",
                                                                    color: "var(--gold)",
                                                                    border: "1px solid var(--gold-light)",
                                                                    fontSize: "0.8rem"
                                                                }}
                                                            >
                                                                Since {founder.year}
                                                            </div>
                                                        </motion.div>
                                                    </div>

                                                    <motion.div
                                                        className="d-flex align-items-center gap-2 mb-2"
                                                        whileHover={{ x: 10 }}
                                                        transition={{ type: "spring", stiffness: 400 }}
                                                    >
                                                        <FontAwesomeIcon icon={faScaleBalanced} style={{ color: "var(--gold)", fontSize: "0.9rem" }} />
                                                        <span style={{ color: "var(--primary-blue)", fontWeight: "500" }}>
                                                            {founder.qualification}
                                                        </span>
                                                    </motion.div>

                                                    <motion.div
                                                        className="d-flex align-items-center gap-2 mb-2"
                                                        whileHover={{ x: 10 }}
                                                        transition={{ type: "spring", stiffness: 400, delay: 0.1 }}
                                                    >
                                                        <FontAwesomeIcon icon={faGraduationCap} style={{ color: "var(--gold)", fontSize: "0.9rem" }} />
                                                        <span style={{ color: "var(--text-primary)" }}>
                                                            {founder.education}
                                                        </span>
                                                    </motion.div>

                                                    <motion.div
                                                        className="d-flex align-items-center gap-2 mb-3"
                                                        whileHover={{ x: 10 }}
                                                        transition={{ type: "spring", stiffness: 400, delay: 0.2 }}
                                                    >
                                                        <FontAwesomeIcon icon={faGem} style={{ color: "var(--gold)", fontSize: "0.9rem" }} />
                                                        <span style={{ color: "var(--gold)", fontWeight: "500" }}>
                                                            {founder.expertise}
                                                        </span>
                                                    </motion.div>
                                                </div>

                                                <motion.p
                                                    className="mb-0 flex-grow-1"
                                                    style={{
                                                        color: "var(--text-primary)",
                                                        lineHeight: 1.7,
                                                        fontSize: "0.95rem"
                                                    }}
                                                    whileHover={{ scale: 1.01 }}
                                                >
                                                    {founder.description}
                                                </motion.p>

                                                <motion.div
                                                    className="mt-4 pt-3 border-top"
                                                    style={{ borderColor: "var(--gold-light)" }}
                                                    initial={{ opacity: 0 }}
                                                    whileInView={{ opacity: 1 }}
                                                    viewport={{ once: true }}
                                                    transition={{ delay: 0.5 }}
                                                >
                                                    <motion.button
                                                        className="btn d-flex align-items-center gap-2 p-0"
                                                        style={{
                                                            color: "var(--gold)",
                                                            fontWeight: "500",
                                                            fontSize: "0.9rem",
                                                            background: "none",
                                                            border: "none"
                                                        }}
                                                        whileHover={{ x: 5 }}
                                                        onClick={() => navigate("/ourteam")}
                                                    >
                                                        View Profile
                                                        <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: "0.8rem" }} />
                                                    </motion.button>
                                                </motion.div>
                                            </div>
                                        </HoverCard>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </motion.div>

                {/* Company Profile Section with enhanced interaction */}
                <motion.div
                    className="row g-5 mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                    variants={containerVariants}
                >
                    <div className="col-lg-8">
                        <HoverCard
                            className="p-4 p-md-5 rounded-4 h-100"
                            style={{
                                backgroundColor: "var(--white)",
                                border: "1px solid var(--gold-light)",
                                boxShadow: "var(--shadow-medium)"
                            }}
                        >
                            <div className="position-relative z-2">
                                <motion.div
                                    className="d-flex align-items-center gap-3 mb-4"
                                    whileHover={{ x: 10 }}
                                >
                                    <motion.div
                                        className="p-3 rounded-3"
                                        style={{
                                            backgroundColor: "rgba(184, 134, 11, 0.1)",
                                            color: "var(--gold)"
                                        }}
                                        whileHover={{ rotate: 360 }}
                                        transition={{ duration: 0.6, ease: "easeInOut" }}
                                    >
                                        <FontAwesomeIcon icon={faBuildingColumns} style={{ fontSize: "1.8rem" }} />
                                    </motion.div>
                                    <h2 className="fw-bold mb-0"
                                        style={{
                                            fontFamily: "'Playfair Display', serif",
                                            color: "var(--primary-dark)"
                                        }}
                                    >
                                        About RNA Law Chambers
                                    </h2>
                                </motion.div>

                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.2 }}
                                >
                                    <p className="fs-5 mb-4" style={{
                                        color: "var(--text-primary)",
                                        lineHeight: 1.8
                                    }}>
                                        RNA Law Chambers is a distinguished legal practice founded by two London-educated
                                        Advocates-on-Record of the Patna High Court. With a  experience spanning
                                        over +18 Years, we provide comprehensive legal services across civil, criminal,
                                        and corporate law domains.
                                    </p>

                                    <div className="mb-4">
                                        <motion.h4
                                            className="fw-bold mb-3"
                                            style={{ color: "var(--primary-dark)" }}
                                            whileHover={{ scale: 1.02 }}
                                        >
                                            Our Core Expertise
                                        </motion.h4>
                                        <p style={{ color: "var(--text-primary)", lineHeight: 1.7 }}>
                                            Being our core area of practice, we have extensive experience in representing
                                            clients in disputes relating to civil, criminal and corporate laws. We handle
                                            litigation across multiple practice areas including Corporate and Commercial
                                            Disputes, Arbitration, Constitutional Law, Criminal Law (Anticipatory Bails,
                                            Regular Bails, Quashing), White-Collar Crimes, Service Laws, Direct and
                                            Indirect Taxation, Intellectual Property, Banking & Securitization Laws,
                                            Consumer Protection Laws, Labour Laws, and Land Disputes.
                                        </p>
                                    </div>

                                    <div className="row g-3 mt-4">
                                        <div className="col-md-6">
                                            <motion.div
                                                className="d-flex align-items-center gap-3 p-3 rounded-3 h-100"
                                                style={{
                                                    backgroundColor: "rgba(184, 134, 11, 0.05)",
                                                    border: "1px solid var(--gold-light)"
                                                }}
                                                whileHover={{
                                                    scale: 1.03,
                                                    backgroundColor: "rgba(184, 134, 11, 0.1)"
                                                }}
                                                transition={{ type: "spring", stiffness: 400 }}
                                            >
                                                <motion.div
                                                    whileHover={{ rotate: 360 }}
                                                    transition={{ duration: 0.6 }}
                                                >
                                                    <FontAwesomeIcon icon={faGlobe} style={{ color: "var(--gold)", fontSize: "1.2rem" }} />
                                                </motion.div>
                                                <div>
                                                    <div className="fw-semibold" style={{ color: "var(--primary-dark)" }}>
                                                        Trade & Sector
                                                    </div>
                                                    <div style={{ color: "var(--text-primary)", fontSize: "0.9rem" }}>
                                                        Legal Services / Law Firm
                                                    </div>
                                                </div>
                                            </motion.div>
                                        </div>
                                        <div className="col-md-6">
                                            <motion.div
                                                className="d-flex align-items-center gap-3 p-3 rounded-3 h-100"
                                                style={{
                                                    backgroundColor: "rgba(44, 62, 80, 0.05)",
                                                    border: "1px solid var(--primary-blue)"
                                                }}
                                                whileHover={{
                                                    scale: 1.03,
                                                    backgroundColor: "rgba(44, 62, 80, 0.1)"
                                                }}
                                                transition={{ type: "spring", stiffness: 400 }}
                                            >
                                                <motion.div
                                                    whileHover={{ rotate: 360 }}
                                                    transition={{ duration: 0.6 }}
                                                >
                                                    <FontAwesomeIcon icon={faCalendarAlt} style={{ color: "var(--primary-blue)", fontSize: "1.2rem" }} />
                                                </motion.div>
                                                <div>
                                                    <div className="fw-semibold" style={{ color: "var(--primary-dark)" }}>
                                                        Year of Establishment - 2008
                                                    </div>
                                                    <div style={{ color: "var(--text-primary)", fontSize: "0.9rem" }}>
                                                        Nikhil Kumar Agrawal<br />
                                                        Aditi Hansaria 
                                                    </div>
                                                </div>
                                            </motion.div>
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </HoverCard>
                    </div>

                    {/* Mission & Vision with enhanced cards */}
                    <div className="col-lg-4">
                        <motion.div
                            className="h-100"
                            variants={containerVariants}
                        >
                            <div className="vstack gap-4 h-100">
                                {/* Mission Card */}
                                <motion.div
                                    className="p-4 rounded-4 flex-grow-1 position-relative overflow-hidden"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: "1px solid var(--gold-light)",
                                        boxShadow: "var(--shadow-soft)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "0 15px 30px rgba(184, 134, 11, 0.1)"
                                    }}
                                    initial={{ opacity: 0, x: 30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6 }}
                                >
                                    <motion.div
                                        className="d-flex align-items-center gap-3 mb-3"
                                        whileHover={{ x: 5 }}
                                    >
                                        <motion.div
                                            className="p-2 rounded-3"
                                            style={{
                                                backgroundColor: "rgba(184, 134, 11, 0.1)",
                                                color: "var(--gold)"
                                            }}
                                            whileHover={{ scale: 1.1 }}
                                        >
                                            <FontAwesomeIcon icon={faBullseye} />
                                        </motion.div>
                                        <h4 className="fw-bold mb-0" style={{ color: "var(--primary-dark)" }}>
                                            Our Mission
                                        </h4>
                                    </motion.div>
                                    <p style={{
                                        color: "var(--text-primary)",
                                        lineHeight: 1.7,
                                        fontStyle: "italic"
                                    }}>
                                        To provide accessible, efficient, and result-oriented legal solutions while
                                        upholding the highest standards of professional ethics and integrity.
                                    </p>
                                </motion.div>

                                {/* Vision Card */}
                                <motion.div
                                    className="p-4 rounded-4 flex-grow-1 position-relative overflow-hidden"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: "1px solid var(--primary-blue)",
                                        boxShadow: "var(--shadow-soft)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        boxShadow: "0 15px 30px rgba(44, 62, 80, 0.1)"
                                    }}
                                    initial={{ opacity: 0, x: 30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: 0.1 }}
                                >
                                    <motion.div
                                        className="d-flex align-items-center gap-3 mb-3"
                                        whileHover={{ x: 5 }}
                                    >
                                        <motion.div
                                            className="p-2 rounded-3"
                                            style={{
                                                backgroundColor: "rgba(44, 62, 80, 0.1)",
                                                color: "var(--primary-blue)"
                                            }}
                                            whileHover={{ scale: 1.1 }}
                                        >
                                            <FontAwesomeIcon icon={faEye} />
                                        </motion.div>
                                        <h4 className="fw-bold mb-0" style={{ color: "var(--primary-dark)" }}>
                                            Our Vision
                                        </h4>
                                    </motion.div>
                                    <p style={{
                                        color: "var(--text-primary)",
                                        lineHeight: 1.7,
                                        fontStyle: "italic"
                                    }}>
                                        To be recognized as a leading law firm in Eastern India, known for excellence
                                        in litigation, client-centric approach, and contribution to the legal profession.
                                    </p>
                                </motion.div>

                                {/* Partners' Message */}
                                <motion.div
                                    className="p-4 rounded-4 flex-grow-1 position-relative overflow-hidden"
                                    style={{
                                        backgroundColor: "rgba(184, 134, 11, 0.03)",
                                        border: "1px solid var(--gold-light)"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        backgroundColor: "rgba(184, 134, 11, 0.08)"
                                    }}
                                    initial={{ opacity: 0, x: 30 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.6, delay: 0.2 }}
                                >
                                    <motion.div
                                        className="d-flex align-items-center gap-3 mb-3"
                                        whileHover={{ x: 5 }}
                                    >
                                        <motion.div
                                            animate={{ rotate: [0, 10, 0] }}
                                            transition={{ duration: 2, repeat: Infinity }}
                                        >
                                            <FontAwesomeIcon icon={faQuoteLeft} style={{ color: "var(--gold)", fontSize: "1.5rem" }} />
                                        </motion.div>
                                        <h4 className="fw-bold mb-0" style={{ color: "var(--primary-dark)" }}>
                                            Partners' Message
                                        </h4>
                                    </motion.div>
                                    <p style={{
                                        color: "var(--text-primary)",
                                        lineHeight: 1.7,
                                        fontSize: "0.95rem"
                                    }}>
                                        "At RNA Law Chambers, we believe that every client deserves personalized
                                        attention and strategic legal counsel. Our commitment is to understand your
                                        unique challenges and provide practical, effective solutions. With our blend
                                        of international legal education and deep local expertise, we strive to exceed
                                        expectations in every matter we undertake."
                                    </p>
                                </motion.div>
                            </div>
                        </motion.div>
                    </div>
                </motion.div>

                {/* Practice Areas Grid with enhanced hover effects */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.1 }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <motion.div
                            className="d-inline-block position-relative"
                            whileHover={{ scale: 1.05 }}
                        >
                            <h2 className="fw-bold mb-3"
                                style={{
                                    fontFamily: "'Playfair Display', serif",
                                    color: "var(--primary-dark)"
                                }}
                            >
                                Comprehensive Legal Services
                            </h2>
                            <motion.div
                                className="position-absolute bottom-0 left-0 w-100"
                                initial={{ scaleX: 0 }}
                                whileInView={{ scaleX: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, ease: "easeOut" }}
                                style={{
                                    height: "3px",
                                    background: "linear-gradient(90deg, transparent, var(--gold), transparent)",
                                    transformOrigin: "center"
                                }}
                            />
                        </motion.div>
                        <motion.p
                            className="text-muted"
                            style={{ maxWidth: "700px", margin: "0 auto" }}
                            whileHover={{ scale: 1.02 }}
                        >
                            We handle litigation across a wide spectrum of legal domains
                        </motion.p>
                    </div>

                    <div className="row g-3">
                        {practiceAreas.map((area, index) => (
                            <motion.div
                                key={index}
                                className="col-6 col-md-4 col-lg-3"
                                variants={itemVariants}
                                onMouseEnter={() => setHoveredArea(index)}
                                onMouseLeave={() => setHoveredArea(null)}
                            >
                                <motion.div
                                    className="p-3 rounded-3 h-100 position-relative overflow-hidden"
                                    style={{
                                        backgroundColor: "var(--white)",
                                        border: hoveredArea === index ? "2px solid var(--gold)" : "1px solid var(--gold-light)",
                                        boxShadow: hoveredArea === index ? "0 10px 30px rgba(184, 134, 11, 0.15)" : "var(--shadow-soft)",
                                        transition: "all 0.3s ease",
                                        cursor: "pointer"
                                    }}
                                    whileHover={{
                                        y: -8,
                                        scale: 1.03
                                    }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    {/* Animated background on hover */}
                                    <motion.div
                                        className="position-absolute top-0 left-0 w-full h-full"
                                        initial={false}
                                        animate={{
                                            opacity: hoveredArea === index ? 0.1 : 0
                                        }}
                                        transition={{ duration: 0.3 }}
                                        style={{
                                            background: "radial-gradient(circle at center, var(--gold) 0%, transparent 70%)"
                                        }}
                                    />

                                    <div className="d-flex align-items-start gap-3 position-relative z-1">
                                        <motion.div
                                            className="p-2 rounded-3 flex-shrink-0"
                                            style={{
                                                backgroundColor: "rgba(184, 134, 11, 0.1)",
                                                color: "var(--gold)"
                                            }}
                                            animate={{
                                                scale: hoveredArea === index ? 1.1 : 1,
                                                rotate: hoveredArea === index ? 5 : 0
                                            }}
                                            transition={{ type: "spring", stiffness: 400 }}
                                        >
                                            <FontAwesomeIcon icon={area.icon} style={{ fontSize: "0.9rem" }} />
                                        </motion.div>
                                        <motion.div
                                            className="fw-medium"
                                            style={{
                                                color: hoveredArea === index ? "var(--gold)" : "var(--primary-dark)",
                                                fontSize: "0.85rem",
                                                lineHeight: 1.4,
                                                transition: "color 0.3s ease"
                                            }}
                                            animate={{
                                                x: hoveredArea === index ? 5 : 0
                                            }}
                                        >
                                            {area.name}
                                        </motion.div>
                                    </div>

                                    {/* Expand indicator */}
                                    <motion.div
                                        className="position-absolute top-3 right-3"
                                        initial={false}
                                        animate={{
                                            opacity: hoveredArea === index ? 1 : 0,
                                            x: hoveredArea === index ? 0 : 10
                                        }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faExternalLinkAlt}
                                            style={{
                                                color: "var(--gold)",
                                                fontSize: "0.7rem"
                                            }}
                                        />
                                    </motion.div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Courts & Tribunals with enhanced interaction */}
                <motion.div
                    className="mb-5 px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.1 }}
                    variants={containerVariants}
                >
                    <div className="text-center mb-5">
                        <motion.h2
                            className="fw-bold mb-3"
                            style={{
                                fontFamily: "'Playfair Display', serif",
                                color: "var(--primary-dark)"
                            }}
                            whileHover={{ scale: 1.02 }}
                        >
                            Courts & Tribunals
                        </motion.h2>
                        <p className="text-muted" style={{ maxWidth: "700px", margin: "0 auto" }}>
                            We appear before various judicial and quasi-judicial authorities across India
                        </p>
                    </div>

                    <div className="row g-3">
                        {courtsTribunals.map((court, index) => (
                            <motion.div
                                key={index}
                                className="col-6 col-md-4 col-lg-3"
                                variants={itemVariants}
                                onMouseEnter={() => setHoveredCourt(index)}
                                onMouseLeave={() => setHoveredCourt(null)}
                            >
                                <motion.div
                                    className="p-3 rounded-3 h-100 d-flex flex-column position-relative overflow-hidden"
                                    style={{
                                        backgroundColor: index < 4 ? "rgba(184, 134, 11, 0.05)" : "var(--white)",
                                        border: hoveredCourt === index ? "2px solid var(--gold)" :
                                            index < 4 ? "1px solid var(--gold)" : "1px solid var(--gold-light)",
                                        transition: "all 0.3s ease",
                                        cursor: "pointer"
                                    }}
                                    whileHover={{
                                        y: -5,
                                        scale: 1.02
                                    }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    {/* Hover line effect */}
                                    <motion.div
                                        className="position-absolute top-0 left-0 w-full"
                                        initial={false}
                                        animate={{
                                            height: hoveredCourt === index ? "100%" : "0%"
                                        }}
                                        transition={{ duration: 0.3 }}
                                        style={{
                                            background: "linear-gradient(transparent, rgba(184, 134, 11, 0.05), transparent)",
                                            zIndex: 0
                                        }}
                                    />

                                    <div className="position-relative z-1">
                                        <motion.div
                                            className="fw-semibold mb-2"
                                            style={{
                                                color: hoveredCourt === index ? "var(--gold)" :
                                                    index < 4 ? "var(--gold)" : "var(--primary-dark)",
                                                fontSize: "0.9rem",
                                                lineHeight: 1.3,
                                                transition: "color 0.3s ease"
                                            }}
                                            animate={{
                                                x: hoveredCourt === index ? 5 : 0
                                            }}
                                        >
                                            {court.name}
                                        </motion.div>
                                        <motion.div
                                            className="small mt-auto"
                                            style={{ color: "var(--gray-medium)" }}
                                            animate={{
                                                opacity: hoveredCourt === index ? 1 : 0.7
                                            }}
                                        >
                                            {court.level}
                                        </motion.div>
                                    </div>

                                    {/* Level indicator */}
                                    <motion.div
                                        className="position-absolute bottom-2 right-2"
                                        initial={false}
                                        animate={{
                                            scale: hoveredCourt === index ? 1.2 : 1
                                        }}
                                    >
                                        <div
                                            className="rounded-circle"
                                            style={{
                                                width: "8px",
                                                height: "8px",
                                                backgroundColor: index < 4 ? "var(--gold)" : "var(--primary-blue)",
                                                opacity: 0.5
                                            }}
                                        />
                                    </motion.div>
                                </motion.div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Our Team & Approach with enhanced animations */}
                <motion.div
                    className="px-3"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={containerVariants}
                >
                    <motion.div
                        className="p-4 p-md-5 rounded-4 position-relative overflow-hidden"
                        style={{
                            backgroundColor: "var(--primary-dark)",
                            backgroundImage: "linear-gradient(135deg, var(--primary-dark) 0%, var(--primary-blue) 100%)",
                            border: "1px solid var(--gold-light)",
                            boxShadow: "0 20px 40px rgba(0,0,0,0.2)"
                        }}
                        whileHover={{
                            boxShadow: "0 30px 60px rgba(0,0,0,0.3)",
                            y: -5
                        }}
                    >
                        {/* Animated background pattern */}
                        <motion.div
                            className="position-absolute top-0 left-0 w-full h-full"
                            style={{
                                backgroundImage: `radial-gradient(circle at 20% 50%, rgba(184, 134, 11, 0.05) 0%, transparent 50%),
                                 radial-gradient(circle at 80% 20%, rgba(184, 134, 11, 0.03) 0%, transparent 50%)`,
                                zIndex: 0
                            }}
                            animate={{
                                backgroundPosition: ["0% 0%", "100% 100%"]
                            }}
                            transition={{
                                duration: 20,
                                repeat: Infinity,
                                ease: "linear"
                            }}
                        />

                        <div className="row align-items-center position-relative z-1">
                            <div className="col-lg-8">
                                <motion.div variants={fadeInUp}>
                                    <h3 className="fw-bold mb-3" style={{ color: "var(--white)" }}>
                                        Our Team & Approach
                                    </h3>
                                    <p className="mb-4" style={{
                                        color: "rgba(255, 255, 255, 0.9)",
                                        lineHeight: 1.7
                                    }}>
                                        We are a dedicated team of lawyers, associates, and support staff committed to
                                        delivering the most trustworthy and satisfying litigation experience. Our partners
                                        bring international legal education from the London School of Economics and
                                        University College London, combined with deep expertise in Indian law and practice.
                                    </p>
                                    <p style={{
                                        color: "rgba(255, 255, 255, 0.9)",
                                        lineHeight: 1.7
                                    }}>
                                        We also aid our clients in resolving disputes through arbitration, mediation
                                        and conciliation, providing comprehensive legal solutions tailored to each
                                        client's unique needs.
                                    </p>

                                    <motion.button
                                        className="btn mt-4 px-4 py-2"
                                        style={{
                                            backgroundColor: "var(--gold)",
                                            color: "var(--white)",
                                            border: "none",
                                            borderRadius: "6px",
                                            fontWeight: "500"
                                        }}
                                        whileHover={{
                                            scale: 1.05,
                                            backgroundColor: "var(--gold-light)"
                                        }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={()=>navigate("/ourteam")}
                                    >
                                        Meet Our Team
                                        <FontAwesomeIcon icon={faChevronRight} className="ms-2" />
                                    </motion.button>
                                </motion.div>
                            </div>
                            <div className="col-lg-4">
                                <motion.div
                                    className="text-center"
                                    variants={scaleIn}
                                    whileHover={{
                                        scale: 1.1,
                                        rotate: 5
                                    }}
                                    transition={{ type: "spring", stiffness: 300 }}
                                >
                                    <div className="p-4 rounded-4 d-inline-block position-relative overflow-hidden"
                                        style={{
                                            backgroundColor: "rgba(184, 134, 11, 0.15)",
                                            border: "2px solid var(--gold)",
                                            backdropFilter: "blur(10px)"
                                        }}
                                    >
                                        {/* Rotating border effect */}
                                        <motion.div
                                            className="position-absolute top-0 left-0 w-full h-full"
                                            animate={{ rotate: 360 }}
                                            transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
                                            style={{
                                                background: "conic-gradient(transparent, var(--gold), transparent)",
                                                borderRadius: "inherit",
                                                mask: "linear-gradient(#0000, #0000) content-box, linear-gradient(#0000, #0000)",
                                                WebkitMask: "linear-gradient(#0000, #0000) content-box, linear-gradient(#0000, #0000)",
                                                padding: "2px",
                                                WebkitMaskComposite: "xor",
                                                maskComposite: "exclude"
                                            }}
                                        />

                                        <FontAwesomeIcon
                                            icon={faAward}
                                            className="mb-3"
                                            style={{ fontSize: "3rem", color: "var(--gold)" }}
                                        />
                                        <div className="display-6 fw-bold mb-2" style={{ color: "var(--white)" }}>
                                            18+ Years
                                        </div>
                                        <motion.div
                                            style={{ color: "var(--gold)" }}
                                            animate={{ opacity: [0.5, 1, 0.5] }}
                                            transition={{ duration: 2, repeat: Infinity }}
                                        >
                                            Combined Experience
                                        </motion.div>
                                    </div>
                                </motion.div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </div>
    );
};